import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// components
import ChatInput from "./ChatInput";
import ChatBox from "./ChatBox";

// constants
import * as statusTypes from "../../constants/status.constants";
// styles
import { ChatStyles } from "./chat.styles";

const EXIT_DURATION = 350;

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const buildDriverName = (driver) => {
  if (!driver) return "driver";
  const first = driver.firstName || driver.first_name || "";
  const last = driver.lastName || driver.last_name || "";
  const name = `${first} ${last}`.trim();
  return name || "driver";
};

const Chat = ({ service, isOpen = true, onClose }) => {
  const [mode, setMode] = useState("text");
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const exitTimer = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (exitTimer.current) {
        clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setShouldRender(true);
      // wait for first paint with width:0, then flip to isVisible to trigger transition
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setIsVisible(true));
      });
    } else {
      setIsVisible(false);
      if (shouldRender) {
        exitTimer.current = setTimeout(() => {
          setShouldRender(false);
          exitTimer.current = null;
        }, EXIT_DURATION);
      }
    }
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, []);

  if (!shouldRender) return null;

  const isInputVisible =
    service.status === statusTypes.IN_PROGRESS ||
    service.status === statusTypes.PAUSED ||
    service.status === statusTypes.ACCEPTED ||
    service.status === statusTypes.PENDING;

  const driverName = buildDriverName(service.driver);
  const isNotif = mode === "notification";

  return createPortal(
    <ChatStyles
      className={`${isVisible ? "isOpen" : ""} ${
        isNotif ? "isNotifMode" : ""
      }`}
    >
      <div className="chatInner">
        <header className="chatHead">
          <div className="chatHeadTitle">Chat with {driverName}</div>
          {onClose && (
            <button
              type="button"
              className="iconBtn"
              onClick={onClose}
              aria-label="Hide chat"
            >
              <ArrowRightIcon />
            </button>
          )}
        </header>

        <div className="chatBox">
          <ChatBox serviceId={service.id} />
        </div>

        {isInputVisible && (
          <div className="inputContainer">
            <ChatInput
              serviceId={service.id}
              mode={mode}
              setMode={setMode}
            />
          </div>
        )}
      </div>
    </ChatStyles>,
    document.body
  );
};

export default Chat;
